export type SupportedCurrency = string;

export type SupportedCountry = string;

type CurrencyFormatOptions = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

const DEFAULT_CURRENCY: SupportedCurrency = 'GHS';
const DEFAULT_COUNTRY: SupportedCountry = 'GH';
const BASE_CURRENCY: SupportedCurrency = 'USD';

// Static FX table for UI estimates in the current POC.
const BASE_TO_TARGET_RATE: Record<string, number> = {
  USD: 1,
  GHS: 15.6,
  NGN: 1650,
};

const COUNTRY_TO_CURRENCY: Record<string, SupportedCurrency> = {
  GH: 'GHS',
  NG: 'NGN',
  US: 'USD',
};

const TIMEZONE_TO_COUNTRY: Record<string, SupportedCountry> = {
  'Africa/Accra': 'GH',
  'Africa/Lagos': 'NG',
  'America/New_York': 'US',
};

const COUNTRY_NAME_TO_CODE: Record<string, SupportedCountry> = {
  ghana: 'GH',
  nigeria: 'NG',
  usa: 'US',
  'united states': 'US',
};

const LOCALE_COUNTRY_REGEX = /^[a-z]{2}-([A-Z]{2})$/;

function asSupportedCountry(countryCode?: string): SupportedCountry | null {
  if (!countryCode) return null;
  const upper = countryCode.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return upper;
  return null;
}

function getCountryFromLocale(locale?: string): SupportedCountry | null {
  if (!locale) return null;
  const match = locale.match(LOCALE_COUNTRY_REGEX);
  if (!match) return null;
  return asSupportedCountry(match[1]);
}

export function detectCountryCode(): SupportedCountry {
  if (typeof window === 'undefined') return DEFAULT_COUNTRY;

  const browserLanguages = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
  for (const locale of browserLanguages) {
    const detected = getCountryFromLocale(locale);
    if (detected) return detected;
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneCountry = TIMEZONE_TO_COUNTRY[timezone];
  if (timezoneCountry) return timezoneCountry;

  return DEFAULT_COUNTRY;
}

export function detectCurrencyCode(): SupportedCurrency {
  const countryCode = detectCountryCode();
  return COUNTRY_TO_CURRENCY[countryCode] || DEFAULT_CURRENCY;
}

export function currencyFromCountryName(country?: string | null): SupportedCurrency {
  if (!country) return DEFAULT_CURRENCY;
  const code = COUNTRY_NAME_TO_CODE[country.trim().toLowerCase()];
  return code ? COUNTRY_TO_CURRENCY[code] : DEFAULT_CURRENCY;
}

export function convertFromBaseCurrency(amount: number, currency: SupportedCurrency): number {
  const targetRate = BASE_TO_TARGET_RATE[currency] ?? 1;
  const baseRate = BASE_TO_TARGET_RATE[BASE_CURRENCY] ?? 1;
  return amount * targetRate / baseRate;
}

export function convertToBaseCurrency(amount: number, currency: SupportedCurrency): number {
  const targetRate = BASE_TO_TARGET_RATE[currency] ?? 1;
  const baseRate = BASE_TO_TARGET_RATE[BASE_CURRENCY] ?? 1;
  return amount * baseRate / targetRate;
}

export function formatCurrency(amount: number, currency: SupportedCurrency, options?: CurrencyFormatOptions): string {
  // Map currency to appropriate locale for proper symbol display
  const localeMap: Record<string, string> = {
    GHS: 'en-GH',
    NGN: 'en-NG',
    USD: 'en-US',
  };
  const locale = localeMap[currency] || 'en-US';
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).format(amount);
}

export function formatPriceFromBase(amount: number, currency: SupportedCurrency, options?: CurrencyFormatOptions): string {
  const converted = convertFromBaseCurrency(amount, currency);
  return formatCurrency(converted, currency, options);
}
